import { useEffect, useState } from "react";
import { mfgMdInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

export function ManufacturingMaterialUseMemberRow({
  member,
  disabled,
  finishVariant,
  onSave,
  onRemove,
  onSetPrimary,
}: {
  member: {
    id: string;
    material_code: string;
    material_name: string;
    material_unit: string;
    on_hand_qty: number;
    preference_rank: number;
    qty_per_use: number;
    is_active: boolean;
    is_primary: boolean;
    finish_code: string | null;
  };
  disabled: boolean;
  finishVariant: boolean;
  onSave: (patch: {
    qty_per_use: number;
    preference_rank: number;
    finish_code?: string | null;
  }) => void;
  onRemove: () => void;
  onSetPrimary: () => void;
}) {
  const [rank, setRank] = useState(String(member.preference_rank));
  const [qty, setQty] = useState(String(member.qty_per_use));
  const [finish, setFinish] = useState(member.finish_code ?? "");

  useEffect(() => {
    setRank(String(member.preference_rank));
    setQty(String(member.qty_per_use));
    setFinish(member.finish_code ?? "");
  }, [member.preference_rank, member.qty_per_use, member.finish_code, member.id]);

  const dirty =
    Number(rank) !== member.preference_rank ||
    Number(qty) !== member.qty_per_use ||
    (finishVariant && finish.trim() !== (member.finish_code ?? ""));

  function commitIfDirty() {
    if (!dirty || disabled || !member.is_active) return;
    const preference_rank = Number(rank);
    const qty_per_use = Number(qty);
    if (!Number.isFinite(preference_rank) || !Number.isFinite(qty_per_use) || qty_per_use <= 0) {
      return;
    }
    onSave({
      preference_rank,
      qty_per_use,
      ...(finishVariant ? { finish_code: finish.trim() || null } : {}),
    });
  }

  return (
    <tr className={member.is_active ? "" : "opacity-50"}>
      <td className="px-2 py-1.5 text-xs">
        <div className="font-medium text-gray-900 dark:text-zinc-100">
          {member.material_code}
          {member.is_primary ? (
            <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-semibold uppercase text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
              Primary
            </span>
          ) : null}
        </div>
        <div className="text-gray-600 dark:text-zinc-400">{member.material_name}</div>
      </td>
      <td className="px-2 py-1.5 text-xs text-gray-600 dark:text-zinc-400">{member.material_unit}</td>
      <td className="px-2 py-1.5 text-xs text-gray-600 dark:text-zinc-400">
        {member.on_hand_qty.toLocaleString()}
      </td>
      {finishVariant ? (
        <td className="px-2 py-1.5">
          <input
            className={mfgMdInputClass + " w-28 font-mono text-xs"}
            value={finish}
            disabled={disabled || !member.is_active}
            placeholder="e.g. gray"
            onChange={(e) => setFinish(e.target.value)}
            onBlur={() => commitIfDirty()}
            title="Finish code matched to deal color modifiers (saves on blur)"
          />
        </td>
      ) : (
        <td className="px-2 py-1.5">
          <input
            type="number"
            min={1}
            max={10}
            className={mfgMdInputClass + " w-20"}
            value={rank}
            disabled={disabled || !member.is_active}
            onChange={(e) => setRank(e.target.value)}
            onBlur={() => commitIfDirty()}
            title="Lower number = preferred (1–10)"
          />
        </td>
      )}
      <td className="px-2 py-1.5">
        <input
          type="number"
          step="any"
          min="0"
          className={mfgMdInputClass + " w-24"}
          value={qty}
          disabled={disabled || !member.is_active}
          onChange={(e) => setQty(e.target.value)}
          onBlur={() => commitIfDirty()}
        />
      </td>
      <td className="px-2 py-1.5 text-right">
        <div className="flex flex-wrap justify-end gap-1">
          {member.is_active && finishVariant && !member.is_primary ? (
            <button
              type="button"
              className="text-xs text-amber-700 dark:text-amber-300"
              disabled={disabled}
              onClick={onSetPrimary}
            >
              Make primary
            </button>
          ) : null}
          {member.is_active && dirty ? (
            <button
              type="button"
              className="text-xs font-semibold text-indigo-600 dark:text-indigo-400"
              disabled={disabled}
              onClick={() => commitIfDirty()}
            >
              Save
            </button>
          ) : null}
          {!member.is_active ? (
            <span className="text-xs text-gray-500 dark:text-zinc-500">Inactive</span>
          ) : null}
          <button
            type="button"
            className="text-xs text-red-600 dark:text-red-400"
            disabled={disabled}
            title="Permanently remove this material from the Part use"
            onClick={onRemove}
          >
            Remove
          </button>
        </div>
      </td>
    </tr>
  );
}
